<script setup lang="ts">
    import { onMounted, reactive, inject } from 'vue';
    import SignedOut from '@cwd/Common/SignedOut.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@shared/components/Form/Form';
    import { registerAccount } from './RegisterAccount.access';
    import { token, $Resources } from '@cwd/lib/main';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { UserEventService } from '@shared/Root/UserEvent.service';
    import { AlertTypes } from '@cwd/shared/Components/BsAlerts';

    const state = reactive<{
        data: {
            Msclid: undefined | string;
            Gclid: undefined | string;
            Currency: undefined | string;
            Brand: undefined | string;
            SalesChannel: undefined | string;
            Referrer: undefined | string;
        };
        form: FormProps;
    }>({
        data: {
            Msclid: undefined,
            Gclid: undefined,
            Currency: undefined,
            Brand: undefined,
            SalesChannel: undefined,
            Referrer: undefined,
        },
        form: {
            disableSubmit: false,
            alert: null,
            data: {
                Email: {
                    modelValue: '',
                    required: true,
                    input: {
                        type: 'email',
                        label: $Resources.RegisterAccountEmail,
                    },
                },
                Name: {
                    modelValue: '',
                    required: true,
                    input: {
                        label: $Resources.RegisterAccountName,
                    },
                },
                Company: {
                    modelValue: '',
                    required: true,
                    input: {
                        label: $Resources.RegisterAccountCompanyOrOrganization,
                    },
                },
                CompanySize: {
                    modelValue: null,
                    show: false,
                    required: true,
                    radioGroup: {
                        legend: '',
                        options: [
                            { label: $Resources.RegisterAccountCompanySizeXsLabel, value: '0' },
                            { label: $Resources.RegisterAccountCompanySizeSmLabel, value: '1-9' },
                            { label: $Resources.RegisterAccountCompanySizeMdLabel, value: '10-100' },
                            { label: $Resources.RegisterAccountCompanySizeLgLabel, value: '100+' },
                        ],
                    },
                },
                Password: {
                    modelValue: '',
                    required: true,
                    input: {
                        label: $Resources.PasswordLabel,
                        type: 'newPassword',
                    },
                },
                Phone: {
                    modelValue: '',
                    isValid: undefined,
                    required: true,
                    inputTel: {
                        label: $Resources.RegisterAccountPhone,
                        type: 'tel',
                    },
                },
            },
        },
    });

    // Get the UserEventService from the injector
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const userEventService = injectorAccess.injector.get(UserEventService);

    const showCompanySizeSelector = () => {
        let data = state.form.data;
        data.CompanySize.show = (data.Company.modelValue as string).length > 0 ? true : false;
        data.CompanySize.radioGroup!.legend = $Resources.RegisterAccountCompanySizeLegend + ' ' + data.Company.modelValue + '?';
    };

    onMounted(async () => {
        setupUrlParams();
    });

    const setupUrlParams = (): void => {
        const location = window.location;
        const url = new URL(location.toString());
        const params = url.searchParams;

        state.data.Currency = params.get('currency') || $Resources.RegistrationDefaultCurrency;
        state.data.Brand = params.get('brand') || undefined;
        state.data.Gclid = params.get('_gac') || undefined;
        state.data.Msclid = params.get('msclid') || undefined;
        state.data.Referrer = params.get('ref') || undefined;
        state.data.SalesChannel = params.get('sc') || undefined;

        let language = params.get('language') || params.get('lang');
        setupCulture(language);

        let email = params.get('email');

        if (email !== null) {
            let data = state.form.data;
            data.Email.modelValue = email;
            data.Name.modelValue = getName(email);
            data.Company.modelValue = getCompany(email);
            showCompanySizeSelector();
        }

        userEventService.registerUserEvent('Registration', 'Registration started', state.data.Currency);
    };

    const onSubmit = (event: Event, formData: any) => {
        if (state.form.disableSubmit) return;
        state.form.disableSubmit = true;

        const combinedData = { ...formData, ...state.data };

        // return console.log('combinedData', combinedData)

        registerAccount(combinedData)
            .then((data: any) => {
                token(data.token);

                // Register event
                if (
                    combinedData.CompanySize == 0 ||
                    combinedData.Email.indexOf('@gmail') > 0 ||
                    combinedData.Email.indexOf('@outlook') > 0 ||
                    combinedData.Email.indexOf('@hotmail') > 0 ||
                    combinedData.Email.indexOf('@icloud') > 0
                ) {
                    document.location = '/Account/RegisteredPrivate';
                } else {
                    userEventService.registerUserEvent('Registration', 'Registration completed', '');
                    document.location = '/Account/Registered';
                }
            })
            .catch((error) => {
                state.form.disableSubmit = false;

                if (error.message.indexOf('User already exists') >= 0) {
                    userEventService.registerUserEvent('Registration', 'Registration failed', 'User exists');
                    state.form.alert = {
                        type: AlertTypes.Danger,
                        title: $Resources.ErrorHeading,
                        content: $Resources.ErrorUserExistsBody,
                    };
                } else if (error.message.indexOf('Password is invalid') >= 0) {
                    userEventService.registerUserEvent('Registration', 'Registration failed', 'Password invalid');
                    state.form.alert = {
                        type: AlertTypes.Danger,
                        title: $Resources.ErrorHeading,
                        content: $Resources.PasswordErrorLabel,
                    };
                } else {
                    userEventService.registerUserEvent('Registration', 'Registration failed', 'Technical issues');
                    state.form.alert = {
                        type: AlertTypes.Danger,
                        title: $Resources.ErrorHeading,
                        content: $Resources.ErrorTechnicalIssuesBody,
                    };
                }
            });
    };

    const onBlur = (key: string) => {
        if (key === 'Company') {
            showCompanySizeSelector();
        }
    };
</script>

<script lang="ts">
    // declare let window: CustomWindow
    declare let Cookies: any;

    const setupCulture = (language: string | null) => {
        if (language !== null) {
            let newCulture = '';
            let oldCulture = Cookies.get('Culture');

            if (language === 'sv' || language === 'se') {
                newCulture = 'sv-SE';
            } else if (language === 'en' || language === 'eng' || language === 'us') {
                newCulture = 'en-US';
            } else if (language === 'nb' || language === 'no') {
                newCulture = 'nb-NO';
            } else if (language === 'da' || language === 'dk') {
                newCulture = 'da-DK';
            } else if (language === 'fr') {
                newCulture = 'fr-FR';
            }

            if (oldCulture !== newCulture) {
                // reload location, removing language&lang param to not reload forever...
                Cookies.set('Culture', newCulture);

                document.location = location.href
                    .replace('?language=' + language + '&', '?')
                    .replace('?lang=' + language + '&', '?')
                    .replace('?language=' + language, '')
                    .replace('?lang=' + language, '')
                    .replace('&language=' + language, '')
                    .replace('&lang=' + language, '');
            }
        }
    };

    const getCompany = function (email: string) {
        let domain = email.split('@')[1];
        if (domain === undefined) return '';
        let companyName = domain.split('.')[0];

        if (
            companyName === 'hotmail' ||
            companyName === 'outlook' ||
            companyName === 'gmail' ||
            companyName === 'yahoo' ||
            companyName === 'live' ||
            companyName === 'aol'
        )
            return '';

        return companyName.charAt(0).toUpperCase() + companyName.slice(1);
    };

    const getName = function (email: string) {
        let firstName = email.split('@')[0].split('.')[0];
        let lastName = email.split('@')[0].split('.')[1];

        let name = firstName.charAt(0).toUpperCase() + firstName.slice(1);
        if (typeof lastName !== undefined) name = name + ' ' + lastName.charAt(0).toUpperCase() + lastName.slice(1);

        return name;
    };
</script>

<template>
    <SignedOut :brand="state.data.Brand">
        <div class="m-xl-t m-xl-b">
            <h1 class="h2 m-0-t">{{ $Resources.RegisterHeader }}</h1>
            <span v-html="$Resources.RegisterFreeIntroduction"></span>
        </div>

        <Form
            :data="state.form.data"
            :alert="state.form.alert"
            :disableSubmit="state.form.disableSubmit"
            @onBlur="(el: string) => onBlur(el)"
            @update:isValid="(key, newVal) => (state.form!.data[key].isValid = newVal)"
            @update:modelValue="(key, newVal) => (state.form!.data[key].modelValue = newVal)"
            @onSubmit="onSubmit">
            <template #button>
                <button type="submit" class="btn btn-cta btn-lg btn-block" :class="state.form.disableSubmit && 'is-busy'" :disabled="state.form.disableSubmit">
                    {{ $Resources.RegisterBtn }}
                </button>
            </template>
        </Form>

        <p class="small alt-link-style m-lg-b" v-html="$Resources.AcceptTermsAndConditionsText"></p>

        <p class="m-lg-b" v-html="$Resources.SignInMessage"></p>
    </SignedOut>
</template>

<style scoped></style>

<style lang="less"></style>

